import React, { useState, useEffect, useContext } from "react"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Input,
  Badge,
  Spinner,
} from "reactstrap"
import Flatpickr from "react-flatpickr"
import "flatpickr/dist/themes/material_blue.css"
import Swal from "sweetalert2"
import Breadcrumb from "components/Common/Breadcrumb"
import { SessionContext } from "context/sessionContext"
import { reAdmissionApi } from "apis/ReAdmissionApi"

const ReAdmission = () => {
  document.title = "Re-Admission"

  const { Session, SessionData } = useContext(SessionContext)
  const activeSessionId = Session || sessionStorage.getItem("SessionId")

  const [loading, setLoading] = useState(false)
  const [reAdmissionList, setReAdmissionList] = useState([])

  // Modal state
  const [modal, setModal] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState({
    id: null,
    session_id: "",
    readmission_start: "",
    readmission_end: "",
    is_active: true,
  })
  const [submitting, setSubmitting] = useState(false)

  // Book Delivery Settings state
  const [bookModal, setBookModal] = useState(false)
  const [bookConfig, setBookConfig] = useState({
    is_delivery_enabled: false,
    delivery_fee: "",
  })
  const [bookConfigLoading, setBookConfigLoading] = useState(false)
  const [bookConfigSaving, setBookConfigSaving] = useState(false)

  // Fetch re-admission list
  const fetchReAdmissionList = async () => {
    if (!activeSessionId) return
    setLoading(true)
    try {
      const res = await reAdmissionApi.getReAdmissionList({
        session_id: parseInt(activeSessionId, 10),
      })
      if (res?.data?.status === 200) {
        setReAdmissionList(res.data.data || [])
      } else {
        setReAdmissionList([])
      }
    } catch (err) {
      console.error("Failed to fetch re-admission list:", err.message)
      setReAdmissionList([])
    } finally {
      setLoading(false)
    }
  }

  // Fetch Book Delivery Config
  const fetchBooksDeliveryConfig = async () => {
    setBookConfigLoading(true)
    try {
      const res = await reAdmissionApi.getBooksDeliveryConfig()
      if (res?.data?.status === 200) {
        setBookConfig({
          is_delivery_enabled: res.data.is_delivery_enabled ?? false,
          delivery_fee: res.data.delivery_fee ?? "",
        })
      }
    } catch (err) {
      console.error("Failed to fetch book delivery config:", err.message)
    } finally {
      setBookConfigLoading(false)
    }
  }

  // Save Book Delivery Config
  const handleSaveBookConfig = async () => {
    if (bookConfig.is_delivery_enabled && (bookConfig.delivery_fee === "" || isNaN(Number(bookConfig.delivery_fee)))) {
      Swal.fire("Validation", "Please enter a valid delivery fee.", "warning")
      return
    }
    setBookConfigSaving(true)
    try {
      const payload = {
        is_delivery_enabled: bookConfig.is_delivery_enabled,
        delivery_fee: bookConfig.is_delivery_enabled ? parseInt(bookConfig.delivery_fee, 10) : 0,
      }
      const res = await reAdmissionApi.setBooksDeliveryConfig(payload)
      if (res?.data?.status === 200) {
        Swal.fire({
          title: "Saved!",
          text: res.data.msg || "Book delivery settings updated successfully.",
          icon: "success",
          confirmButtonColor: "#556ee6",
        })
        setBookConfig(prev => ({
          ...prev,
          delivery_fee: res.data.delivery_fee ?? prev.delivery_fee,
          is_delivery_enabled: res.data.is_delivery_enabled ?? prev.is_delivery_enabled,
        }))
        setBookModal(false)
      } else {
        Swal.fire("Error", res?.data?.msg || "Failed to update book delivery settings.", "error")
      }
    } catch (err) {
      console.error("Save book config failed:", err.message)
      Swal.fire("Error", "Something went wrong. Please try again.", "error")
    } finally {
      setBookConfigSaving(false)
    }
  }

  useEffect(() => {
    if (activeSessionId) {
      fetchReAdmissionList()
    }
    fetchBooksDeliveryConfig()
  }, [activeSessionId])

  // Open modal for Add
  const handleAdd = () => {
    setIsEditing(false)
    setFormData({
      id: null,
      session_id: activeSessionId ? parseInt(activeSessionId, 10) : "",
      readmission_start: "",
      readmission_end: "",
      is_active: true,
    })
    setModal(true)
  }

  // Open modal for Edit
  const handleEdit = item => {
    setIsEditing(true)
    // Normalize incoming dates to plain YYYY-MM-DD so Flatpickr can parse and
    // display them reliably (the API may return full datetime/ISO strings).
    const normalize = val => {
      if (!val) return ""
      const d = new Date(val)
      return isNaN(d.getTime()) ? "" : toLocalDate(d)
    }
    setFormData({
      id: item.id,
      session_id: item.session_id,
      readmission_start: normalize(item.readmission_start),
      readmission_end: normalize(item.readmission_end),
      is_active: item.is_active,
    })
    setModal(true)
  }

  // Submit form (Add / Edit)
  const handleSubmit = async () => {
    if (!formData.session_id) {
      Swal.fire("Validation", "Please select a session.", "warning")
      return
    }
    if (!formData.readmission_start) {
      Swal.fire("Validation", "Please select a start date.", "warning")
      return
    }
    if (!formData.readmission_end) {
      Swal.fire("Validation", "Please select an end date.", "warning")
      return
    }
    if (formData.readmission_start > formData.readmission_end) {
      Swal.fire("Validation", "Start date cannot be after end date.", "warning")
      return
    }

    setSubmitting(true)
    try {
      const payload = {
        session_id: parseInt(formData.session_id, 10),
        readmission_start: formData.readmission_start,
        readmission_end: formData.readmission_end,
        is_active: formData.is_active,
      }

      // Include id only when editing
      if (isEditing && formData.id) {
        payload.id = formData.id
      }

      const res = await reAdmissionApi.addReAdmission(payload)

      if (res?.data?.status === 200) {
        Swal.fire({
          title: isEditing ? "Updated!" : "Created!",
          text: res.data.msg || `Re-admission config ${isEditing ? "updated" : "created"} successfully.`,
          icon: "success",
          confirmButtonColor: "#556ee6",
        })
        setModal(false)
        fetchReAdmissionList()
      } else {
        Swal.fire("Error", res?.data?.msg || "Failed to save re-admission config.", "error")
      }
    } catch (err) {
      console.error("Submit failed:", err.message)
      Swal.fire("Error", "Something went wrong. Please try again.", "error")
    } finally {
      setSubmitting(false)
    }
  }

  // Format a Date object as a local YYYY-MM-DD string (avoids the UTC
  // day-shift bug that toISOString() causes in timezones like IST).
  const toLocalDate = date => {
    const yyyy = date.getFullYear()
    const mm = String(date.getMonth() + 1).padStart(2, "0")
    const dd = String(date.getDate()).padStart(2, "0")
    return `${yyyy}-${mm}-${dd}`
  }

  // Returns local midnight for a given Date (strips the time component).
  const startOfDay = date => {
    const d = new Date(date)
    d.setHours(0, 0, 0, 0)
    return d
  }

  // Flatpickr onDayCreate hook: disable any day before today so past dates
  // can't be selected, while still allowing a pre-filled past value to show.
  const disablePastDay = (dObj, dStr, fp, dayElem) => {
    const today = startOfDay(new Date())
    if (startOfDay(dayElem.dateObj) < today) {
      dayElem.classList.add("flatpickr-disabled")
    }
  }

  // Builds an onDayCreate hook for the end-date picker: disables days that are
  // in the past OR on/before the selected start date.
  const makeDisableEndDay = startStr => (dObj, dStr, fp, dayElem) => {
    const today = startOfDay(new Date())
    const cell = startOfDay(dayElem.dateObj)
    if (cell < today) {
      dayElem.classList.add("flatpickr-disabled")
      return
    }
    if (startStr) {
      const start = startOfDay(new Date(startStr))
      if (cell <= start) {
        dayElem.classList.add("flatpickr-disabled")
      }
    }
  }

  // Format date for display
  const formatDate = dateStr => {
    if (!dateStr) return "—"
    const date = new Date(dateStr)
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
  }

  return (
    <React.Fragment>
      <style>
        {`
          .flatpickr-day.flatpickr-disabled,
          .flatpickr-day.flatpickr-disabled:hover {
            color: rgba(0, 0, 0, 0.3) !important;
            cursor: not-allowed;
          }
        `}
      </style>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="" breadcrumbItem="Re-Admission" />

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Row className="mb-3">
                    <Col className="d-flex justify-content-between align-items-center">
                      <h4 className="card-title mb-0">Re-Admission Configuration</h4>
                      <div className="d-flex gap-2">
                        <Button
                          color="secondary"
                          outline
                          onClick={() => setBookModal(true)}
                          title="Book Delivery Settings"
                        >
                          <i className="bx bx-package me-1" />
                          Book Delivery
                        </Button>
                        <Button color="primary" onClick={handleAdd}>
                          <i className="bx bx-plus me-1" />
                          Add Re-Admission
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  <div className="table-responsive">
                    {loading ? (
                      <div className="text-center py-5">
                        <Spinner color="primary" />
                        <p className="mt-2 text-muted">Loading re-admission data…</p>
                      </div>
                    ) : (
                      <Table className="table align-middle table-nowrap table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>Start Date</th>
                            <th>End Date</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reAdmissionList.length > 0 ? (
                            reAdmissionList.map((item, index) => (
                              <tr key={item.id}>
                                <td>{index + 1}</td>
                                <td>{formatDate(item.readmission_start)}</td>
                                <td>{formatDate(item.readmission_end)}</td>
                                <td>
                                  <Badge
                                    color={item.is_active ? "success" : "danger"}
                                    className="font-size-12 px-3 py-2"
                                  >
                                    {item.is_active ? "Active" : "Expired"}
                                  </Badge>
                                </td>
                                <td>
                                  <Button
                                    color="info"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => handleEdit(item)}
                                  >
                                    <i className="bx bx-edit-alt me-1" />
                                    Edit
                                  </Button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={5} className="text-center py-4 text-muted">
                                No re-admission configuration found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

        </Container>
      </div>

      {/* Add / Edit Modal */}
      <Modal isOpen={modal} toggle={() => setModal(!modal)} centered>
        <ModalHeader toggle={() => setModal(!modal)}>
          {isEditing ? "Edit Re-Admission" : "Add Re-Admission"}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <Label className="form-label">Re-Admission Start Date</Label>
            <Flatpickr
              key={`start-${formData.id ?? "new"}`}
              className="form-control"
              placeholder="Select start date"
              value={formData.readmission_start}
              options={{
                dateFormat: "Y-m-d",
                altInput: true,
                altFormat: "d M Y",
                // Note: we intentionally do NOT set `minDate` here. Setting it
                // would cause Flatpickr to clear a stored past date on init,
                // leaving the field blank while editing. Instead we disable
                // past day cells via onDayCreate so the existing date still
                // shows, but the user cannot select a date earlier than today.
                onDayCreate: disablePastDay,
              }}
              onChange={([date]) => {
                if (date) {
                  const formatted = toLocalDate(date)
                  setFormData(prev => ({
                    ...prev,
                    readmission_start: formatted,
                    // Reset end date if it's before the new start date
                    readmission_end:
                      prev.readmission_end && prev.readmission_end < formatted
                        ? ""
                        : prev.readmission_end,
                  }))
                }
              }}
            />
          </div>

          <div className="mb-3">
            <Label className="form-label">Re-Admission End Date</Label>
            <Flatpickr
              key={formData.readmission_start}
              className="form-control"
              placeholder="Select end date"
              value={formData.readmission_end}
              options={{
                dateFormat: "Y-m-d",
                altInput: true,
                altFormat: "d M Y",
                // Same reasoning as the start field: no `minDate` so a stored
                // past end date still displays while editing. We disable day
                // cells that are in the past OR on/before the start date.
                onDayCreate: makeDisableEndDay(formData.readmission_start),
              }}
              onChange={([date]) => {
                if (date) {
                  const formatted = toLocalDate(date)
                  setFormData(prev => ({ ...prev, readmission_end: formatted }))
                }
              }}
            />
          </div>

          
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModal(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <Spinner size="sm" className="me-1" />
                Saving...
              </>
            ) : isEditing ? (
              "Update"
            ) : (
              "Create"
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Book Delivery Settings Modal */}
      <Modal isOpen={bookModal} toggle={() => setBookModal(!bookModal)} centered>
        <ModalHeader toggle={() => setBookModal(false)}>
          <i className="bx bx-package me-2" />
          Book Delivery Settings
        </ModalHeader>
        <ModalBody>
          {bookConfigLoading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
              <p className="mt-2 text-muted">Loading delivery settings…</p>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <div className="d-flex align-items-center justify-content-between">
                  <Label className="form-label mb-0 fw-semibold">Enable Book Delivery</Label>
                  <div className="d-flex align-items-center gap-2">
                    <div className="form-check form-switch form-switch-md mb-0">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id="bookDeliveryToggle"
                        checked={bookConfig.is_delivery_enabled}
                        onChange={e =>
                          setBookConfig(prev => ({
                            ...prev,
                            is_delivery_enabled: e.target.checked,
                          }))
                        }
                      />
                    </div>
                    <Badge
                      color={bookConfig.is_delivery_enabled ? "success" : "danger"}
                      className="font-size-12 px-3 py-1"
                    >
                      {bookConfig.is_delivery_enabled ? "Enabled" : "Disabled"}
                    </Badge>
                  </div>
                </div>
              </div>

              {bookConfig.is_delivery_enabled && (
                <div className="mb-3">
                  <Label className="form-label fw-semibold" htmlFor="modalDeliveryFee">
                    Delivery Fee (&#8377;) <span style={{color:"red"}}>*</span>
                  </Label>
                  <Input
                    type="number"
                    id="modalDeliveryFee"
                    className="form-control"
                    placeholder="Enter delivery fee amount"
                    min={0}
                    value={bookConfig.delivery_fee}
                    onChange={e =>
                      setBookConfig(prev => ({
                        ...prev,
                        delivery_fee: e.target.value,
                      }))
                    }
                  />
                </div>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setBookModal(false)}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSaveBookConfig}
            disabled={bookConfigSaving || bookConfigLoading}
          >
            {bookConfigSaving ? (
              <>
                <Spinner size="sm" className="me-1" />
                Saving...
              </>
            ) : (
              <>
                <i className="bx bx-save me-1" />
                Save Settings
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  )
}

export default ReAdmission
